import * as React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Finding, CorrectiveAction } from "@/app/pages/auditcentre";
import { CorrectiveActionsDialog } from "./CorrectiveActionsDialog";
import { useTenancy } from "@/contexts/TenancyContext";

interface FindingDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  finding: Finding | null;
  onActionCreated: (action: CorrectiveAction) => void;
  onActionUpdated: (actionId: string, updates: Partial<CorrectiveAction>) => void;
  onActionDeleted: (actionId: string) => void;
  auditService: any; // You might want to type this properly
}

export function FindingDetailDialog({
  open,
  onOpenChange,
  finding: initialFinding,
  onActionCreated,
  onActionUpdated,
  onActionDeleted,
  auditService
}: FindingDetailDialogProps) {
  const { userIdToName, activeTenancy } = useTenancy();
  const [showCorrectiveActions, setShowCorrectiveActions] = React.useState(false);
  const [finding, setFinding] = React.useState<Finding | null>(initialFinding);
  const [isLoading, setIsLoading] = React.useState(false);

  
  // Update local state when the finding prop changes
  React.useEffect(() => {
    setFinding(initialFinding);
    if (initialFinding?.id) {
      loadCorrectiveActions(initialFinding.id);
    }
  }, [initialFinding]);
  
  const loadCorrectiveActions = async (findingId: string) => {
    if (!auditService) return;
    
    try {
      setIsLoading(true);
      const actions = await auditService.getCorrectiveActionsForFinding(findingId);
      setFinding(prev => prev ? { ...prev, correctiveActions: actions } : null);
    } catch (error) {
      console.error("Error loading corrective actions:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!finding) return null;

  const handleActionCreated = async (action: CorrectiveAction) => {
    if (!auditService || !finding) return;
    
    try {
      // Create the corrective action in Firestore via the audit service
      const newAction = await auditService.createStandaloneCorrectiveAction({
        ...action,
        findingId: finding.id,
        auditId: finding.auditId,
        status: 'Open',
        owner: action.owner || 'Unassigned',
        summary: action.summary || 'No summary provided',
      });
      
      // Update local state with the created action (which includes the Firestore ID)
      finding.correctiveActions = [...(finding.correctiveActions || []), newAction];
      
      // Notify parent component
      onActionCreated(newAction);
      
      // Reload corrective actions to ensure we have the latest data
      await loadCorrectiveActions(finding.id);
      
      return newAction;
    } catch (error) {
      console.error('Error creating corrective action:', error);
      throw error;
    }
  };

  const handleActionUpdated = async (actionId: string, updates: Partial<CorrectiveAction>) => {
    if (!auditService) return;
    
    try {
      // Update the corrective action in Firestore
      await auditService.updateStandaloneCorrectiveAction(actionId, updates);
      
      // Update local state
      if (finding?.correctiveActions) {
        finding.correctiveActions = finding.correctiveActions.map(ca => 
          ca.id === actionId ? { ...ca, ...updates } : ca
        );
      }
      
      // Notify parent component
      onActionUpdated(actionId, updates);
      
      // Reload corrective actions to ensure we have the latest data
      if (finding) {
        await loadCorrectiveActions(finding.id);
      }
    } catch (error) {
      console.error('Error updating corrective action:', error);
      throw error;
    }
  };

  const handleActionDeleted = async (actionId: string) => {
    if (!auditService) return;
    
    try {
      // Delete the corrective action from Firestore
      await auditService.deleteStandaloneCorrectiveAction(actionId);
      
      // Update local state
      if (finding?.correctiveActions) {
        finding.correctiveActions = finding.correctiveActions.filter(ca => ca.id !== actionId);
      }
      
      // Notify parent component
      onActionDeleted(actionId);
    } catch (error) {
      console.error('Error deleting corrective action:', error);
      throw error;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-[80vw]! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
          <div className="flex flex-col gap-4 h-full bg-card">
            <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
              <DialogTitle className="text-foreground">Finding Details</DialogTitle>
              <DialogDescription>ID: {(typeof finding.id === 'string' || typeof finding.id === 'number') ? finding.id : '—'}</DialogDescription>
            </div>
          <div className="flex px-4 pb-4">
            {/* Left side - Finding Details */}
            <div className="w-1/2 pr-6 border-r">
              <DialogHeader>
                <DialogTitle>Overview</DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-3">
                <div className="flex items-center">
                  <span className="font-medium w-24">Type:</span>
                  <Badge>{finding.type}</Badge>
                </div>
                <div className="flex items-center">
                  <span className="font-medium w-24">Status:</span>
                  <Badge variant={finding.status === "Open" ? "destructive" : "success"}>
                    {finding.status}
                  </Badge>
                </div>
                <div>
                  <p className="font-medium">Owner:</p>
                  <p>{userIdToName[finding.owner] === "" || userIdToName[finding.owner] === undefined ? finding.owner : userIdToName[finding.owner]}</p>
                </div>
                <div>
                  <p className="font-medium">Summary:</p>
                  <p>{finding.summary || 'No summary provided'}</p>
                </div>
                <div>
                  <p className="font-medium">Comments:</p>
                  <p>{finding.comments || 'No comments provided'}</p>
                </div>
              </div>
              
              <div className="mt-6">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setShowCorrectiveActions(true)}
                >
                  View/Manage Corrective Actions
                </Button>
              </div>
            </div>

            {/* Right side - Corrective Actions Preview */}
            <div className="w-1/2 pl-6 space-y-6">
              <DialogHeader>
                <DialogTitle>Corrective Actions</DialogTitle>
                <DialogDescription>
                  {finding.correctiveActions?.length || 'No'} action{finding.correctiveActions?.length !== 1 ? 's' : ''} linked
                </DialogDescription>
              </DialogHeader>
              
              {finding.correctiveActions && finding.correctiveActions.length > 0 ? (
                <div className="mt-4 space-y-3 max-h-96 overflow-y-auto pr-2">
                  {finding.correctiveActions.map(action => (
                    <div 
                      key={action.id}
                      className="p-3 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                      onClick={() => {
                        // This would open the edit dialog for the specific action
                        // You might want to implement this
                      }}
                    >
                      <div className="font-medium">{action.summary}</div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground mt-1">
                        <span>Status: {action.status}</span>
                        <span>Owner: {userIdToName[action.owner] === "" || userIdToName[action.owner] === undefined ? action.owner : userIdToName[action.owner]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-8 text-center text-muted-foreground">
                  <p>No corrective actions linked to this finding.</p>
                  <Button 
                    variant="ghost" 
                    className="mt-2"
                    onClick={() => setShowCorrectiveActions(true)}
                  >
                    Add Corrective Action
                  </Button>
                </div>
              )}

            </div>
          </div>
          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Corrective Actions Dialog - Opens side-by-side */}
      <CorrectiveActionsDialog
        open={showCorrectiveActions}
        onOpenChange={setShowCorrectiveActions}
        finding={finding}
        onActionCreated={handleActionCreated}
        onActionUpdated={handleActionUpdated}
        onActionDeleted={handleActionDeleted}
      />
    </>
  );
}
